import { createTheme, type Theme } from '@mui/material/styles'
import type { Localization } from '@mui/material/locale'
import {
  affexyColors,
  affexyFonts,
  affexyGradients,
  affexyRadii,
  affexyShadows,
  affexyTokens,
  type AffexyTokens,
} from './affexyTokens'

// ── Module augmentation: expose brand tokens + a `navy` palette on the theme ─
declare module '@mui/material/styles' {
  interface Theme {
    affexy: AffexyTokens
  }
  interface ThemeOptions {
    affexy?: AffexyTokens
  }
  interface Palette {
    navy: Palette['primary']
  }
  interface PaletteOptions {
    navy?: PaletteOptions['primary']
  }
}

/**
 * Affexy researcher theme — a light "data-confident" canvas with a coral
 * accent. Built once here and applied to the new researcher chrome via a
 * nested ThemeProvider, so it can later become the app-wide default without
 * touching every screen up front.
 */
export function createAffexyTheme(locale?: Localization): Theme {
  const c = affexyColors

  return createTheme(
    {
      affexy: affexyTokens,
      palette: {
        mode: 'light',
        primary: { main: c.coral, dark: c.coralHover, light: c.coral300, contrastText: c.white },
        navy: { main: c.navy, dark: c.navy800, light: c.navy600, contrastText: c.white },
        success: { main: c.success, contrastText: c.white },
        warning: { main: c.warning, contrastText: c.white },
        error: { main: c.danger, contrastText: c.white },
        info: { main: c.gray300, contrastText: c.ink },
        background: { default: c.gray50, paper: c.white },
        text: { primary: c.ink, secondary: c.ink56, disabled: c.ink32 },
        divider: c.ink08,
      },
      shape: { borderRadius: affexyRadii.md },
      typography: {
        fontFamily: affexyFonts.body,
        h1: { fontFamily: affexyFonts.heading, fontWeight: 700, letterSpacing: '-0.015em' },
        h2: { fontFamily: affexyFonts.heading, fontWeight: 700, letterSpacing: '-0.01em' },
        h3: { fontFamily: affexyFonts.heading, fontWeight: 700, letterSpacing: '-0.01em' },
        h4: { fontFamily: affexyFonts.heading, fontWeight: 700 },
        h5: { fontFamily: affexyFonts.heading, fontWeight: 700 },
        h6: { fontFamily: affexyFonts.heading, fontWeight: 700, letterSpacing: '-0.005em' },
        button: { fontFamily: affexyFonts.body, fontWeight: 500, textTransform: 'none' },
        overline: { fontFamily: affexyFonts.body, fontWeight: 700, letterSpacing: '0.08em' },
      },
      components: {
        MuiPaper: { defaultProps: { elevation: 0 } },
        MuiButton: {
          defaultProps: { disableElevation: true },
          styleOverrides: {
            root: { borderRadius: affexyRadii.pill, fontWeight: 500 },
            sizeSmall: { minHeight: 36 },
          },
        },
        MuiChip: {
          styleOverrides: { root: { borderRadius: affexyRadii.pill, fontWeight: 500 } },
        },
        MuiCssBaseline: {
          styleOverrides: {
            '@keyframes affexy-shimmer': {
              '0%': { backgroundPosition: '200% 0' },
              '100%': { backgroundPosition: '-200% 0' },
            },
            '@keyframes affexy-pulse': {
              '0%, 100%': { opacity: 1 },
              '50%': { opacity: 0.35 },
            },
          },
        },
      },
    },
    locale ?? {},
  )
}

export { affexyGradients, affexyShadows }
